import { Component, OnInit, TemplateRef, signal, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { WhatsappSettingsService, WhatsAppNewConnectionsDefaultsPayload, WhatsAppUpdateAllConnectionsSettingsPayload, WahaLogLevel, WAHA_IMAGE_SOURCES, DEFAULT_WAHA_IMAGE_PREFIX, resolveWahaImagePrefix, extractWahaImageTag, wahaImageSourcesIncluding } from '../../services/whatsapp-settings.service';

@Component({
  standalone: false,
  selector: 'app-whatsapp-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.css']
})
export class GlobalSettingsComponent implements OnInit {
  readonly confirmUpdateAllModal = viewChild.required<TemplateRef<any>>('confirmUpdateAllModal');

  // Ganha a origem dos defaults quando eles apontam para um repositório fora da lista conhecida.
  public readonly wahaImageSources = signal<string[]>([...WAHA_IMAGE_SOURCES]);
  public readonly niboWahaImagePrefix: string;

  public form!: FormGroup;
  public newConnectionsForm!: FormGroup;
  public readonly saving = signal(false);
  public readonly defaultsLoading = signal(false);
  public readonly defaultsSaving = signal(false);

  public readonly logLevels: WahaLogLevel[] = ['warn', 'info'];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly toastrService: ToastrService,
    private readonly settingsService: WhatsappSettingsService
  ) {
    this.niboWahaImagePrefix = this.settingsService.niboWahaImagePrefix;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      wahaImageSource: [DEFAULT_WAHA_IMAGE_PREFIX, [Validators.required]],
      wahaImageTag: ['', [Validators.required]],
      wahaLogLevel: ['warn', [Validators.required]],
      niboWahaImageTag: ['', [Validators.required]],
      niboWahaChartVersion: ['', [Validators.required]],
      vpaConfigWAHAMinAllowedMemory: ['', [Validators.required]],
      vpaConfigWAHAMaxAllowedMemory: ['', [Validators.required]]
    });

    this.newConnectionsForm = this.formBuilder.group({
      wahaImageSource: [DEFAULT_WAHA_IMAGE_PREFIX, [Validators.required]],
      wahaImageTag: ['', [Validators.required]],
      niboWahaImageTag: ['', [Validators.required]],
      niboWahaChartVersion: ['', [Validators.required]],
      vpaConfigWAHAMinAllowedMemory: ['', [Validators.required]],
      vpaConfigWAHAMaxAllowedMemory: ['', [Validators.required]]
    });

    this.loadNewConnectionsDefaults();
  }

  private loadNewConnectionsDefaults(): void {
    this.defaultsLoading.set(true);
    this.settingsService.getNewConnectionsDefaults().subscribe({
      next: (defaults) => {
        const wahaImageSource = resolveWahaImagePrefix(defaults?.wahaImage);
        const wahaImageTag = extractWahaImageTag(defaults?.wahaImage);
        this.wahaImageSources.set(wahaImageSourcesIncluding(wahaImageSource));
        const niboWahaImageTag = this.extractImageTag(defaults?.niboWAHAImage, this.niboWahaImagePrefix);
        const niboWahaChartVersion = defaults?.niboWAHAChartVersion ?? '';
        const vpaMin = defaults?.vpaConfigWAHAMinAllowedMemory ?? '';
        const vpaMax = defaults?.vpaConfigWAHAMaxAllowedMemory ?? '';

        this.newConnectionsForm.patchValue({
          wahaImageSource,
          wahaImageTag,
          niboWahaImageTag,
          niboWahaChartVersion,
          vpaConfigWAHAMinAllowedMemory: vpaMin,
          vpaConfigWAHAMaxAllowedMemory: vpaMax
        }, { emitEvent: false });

        this.form.patchValue({
          wahaImageSource,
          wahaImageTag,
          niboWahaImageTag,
          niboWahaChartVersion,
          vpaConfigWAHAMinAllowedMemory: vpaMin,
          vpaConfigWAHAMaxAllowedMemory: vpaMax
        }, { emitEvent: false });
      },
      error: () => { }
    }).add(() => {
      this.defaultsLoading.set(false);
    });
  }

  public saveNewConnectionsDefaults(): void {
    if (this.defaultsSaving()) {
      return;
    }

    this.newConnectionsForm.markAllAsTouched();
    if (this.newConnectionsForm.invalid) {
      return;
    }

    const wahaImagePrefix = String(this.newConnectionsForm.value.wahaImageSource ?? DEFAULT_WAHA_IMAGE_PREFIX);
    const wahaImageTag = String(this.newConnectionsForm.value.wahaImageTag ?? '').trim();
    const niboWahaImageTag = String(this.newConnectionsForm.value.niboWahaImageTag ?? '').trim();

    const payload: WhatsAppNewConnectionsDefaultsPayload = {
      wahaImage: `${wahaImagePrefix}${wahaImageTag}`,
      niboWAHAImage: `${this.niboWahaImagePrefix}${niboWahaImageTag}`,
      niboWAHAChartVersion: String(this.newConnectionsForm.value.niboWahaChartVersion ?? '').trim(),
      vpaConfigWAHAMinAllowedMemory: String(this.newConnectionsForm.value.vpaConfigWAHAMinAllowedMemory ?? '').trim(),
      vpaConfigWAHAMaxAllowedMemory: String(this.newConnectionsForm.value.vpaConfigWAHAMaxAllowedMemory ?? '').trim()
    };

    this.defaultsSaving.set(true);
    this.settingsService.setNewConnectionsDefaults(payload).subscribe({
      next: () => {
        this.toastrService.success('Padrão para novas conexões salvo');
      },
      error: () => {
        this.toastrService.error('Falha ao salvar padrão para novas conexões');
      }
    }).add(() => {
      this.defaultsSaving.set(false);
    });
  }

  private extractImageTag(image: string | undefined, expectedPrefix: string): string {
    const value = String(image ?? '').trim();
    if (!value) {
      return '';
    }

    if (value.startsWith(expectedPrefix)) {
      return value.substring(expectedPrefix.length).trim();
    }

    const parts = value.split(':').filter(p => p.length > 0);
    return parts.length > 1 ? parts[parts.length - 1].trim() : '';
  }

  public openConfirmUpdateAll(): void {
    if (this.saving()) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.modalService.open(this.confirmUpdateAllModal(), { centered: true });
  }

  public confirmUpdateAll(modal: any): void {
    if (this.saving()) {
      return;
    }

    const wahaImagePrefix = String(this.form.value.wahaImageSource ?? DEFAULT_WAHA_IMAGE_PREFIX);
    const wahaImageTag = String(this.form.value.wahaImageTag ?? '').trim();
    const niboWahaImageTag = String(this.form.value.niboWahaImageTag ?? '').trim();

    const payload: WhatsAppUpdateAllConnectionsSettingsPayload = {
      wahaImage: `${wahaImagePrefix}${wahaImageTag}`,
      wahaLogLevel: this.form.value.wahaLogLevel,
      wahaEnableTagEvents: false,
      niboWahaImage: `${this.niboWahaImagePrefix}${niboWahaImageTag}`,
      niboWahaChartVersion: String(this.form.value.niboWahaChartVersion ?? '').trim(),
      vpaConfigWAHAMinAllowedMemory: String(this.form.value.vpaConfigWAHAMinAllowedMemory ?? '').trim(),
      vpaConfigWAHAMaxAllowedMemory: String(this.form.value.vpaConfigWAHAMaxAllowedMemory ?? '').trim()
    };
    this.saving.set(true);

    this.settingsService.updateAllConnections(payload).subscribe({
      next: () => {
        modal.close();
        this.toastrService.success('Atualização global enviada. As conexões serão atualizadas e reiniciadas.');
      },
      error: () => {
        this.toastrService.error('Falha ao enviar atualização global');
      }
    }).add(() => {
      this.saving.set(false);
    });
  }
}
